/**
 * ResizableViewport
 *
 * 通用"可拖动调整高度"视口容器。适用于嵌入式 iframe、xterm 终端、
 * tldraw 画布、长滚动列表等"固定高度但用户经常想拉大"的内容。
 *
 * 特性：
 * - 默认非受控，内部维护高度；也可受控（传 `height` + `onHeightChange`）。
 * - 可选 `storageKey` 持久化到 localStorage。
 * - 默认底部抓手，可改为顶部或两端。
 * - 拖动时叠加透明 overlay，防止 iframe / xterm 抢鼠标事件。
 * - 高度变化默认平滑过渡；拖动时关闭过渡。
 */

import {
    type CSSProperties,
    type ReactNode,
    useCallback,
    useEffect,
    useRef,
    useState,
} from "react";
import { usePluginSdkT } from "../i18n";

/** Inline horizontal grip icon (avoids depending on lucide-react in the SDK). */
function GripIcon({ className }: { className?: string }) {
    return (
        <svg
            width={14}
            height={14}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
            className={className}
            aria-hidden="true"
        >
            <circle cx={9} cy={6} r={1} />
            <circle cx={9} cy={12} r={1} />
            <circle cx={9} cy={18} r={1} />
            <circle cx={15} cy={6} r={1} />
            <circle cx={15} cy={12} r={1} />
            <circle cx={15} cy={18} r={1} />
        </svg>
    );
}

export type ResizableViewportHandlePosition = "bottom" | "top" | "both";

export interface ResizableViewportProps {
    /**
     * Fill the available parent height instead of using a fixed/resizable pixel
     * height. Intended for host fullscreen/fill-height render layouts.
     */
    fillAvailable?: boolean;
    /** 默认高度（非受控模式下使用）。默认 400。 */
    defaultHeight?: number;
    /** 最小高度，默认 100。 */
    min?: number;
    /** 最大高度（可选）。不传则不限制。 */
    max?: number;
    /** 受控高度。传入此值时组件变为受控模式。 */
    height?: number;
    /** 高度变化回调（拖拽过程中也会触发）。 */
    onHeightChange?: (height: number) => void;
    /** 拖动结束时的回调（适合写入持久化配置）。 */
    onHeightCommit?: (height: number) => void;
    /** localStorage 持久化键（仅非受控模式生效）。 */
    storageKey?: string;
    /** 抓手位置，默认 "bottom"。 */
    handlePosition?: ResizableViewportHandlePosition;
    /**
     * 拖动时是否在内容区叠加透明 overlay 以阻止子元素（iframe / xterm）
     * 抢占鼠标事件。默认 true。
     */
    blockPointerWhileDragging?: boolean;
    /** 抓手提示文本，默认 "拖动调整高度"。 */
    title?: string;
    /** 外层容器额外类名。 */
    className?: string;
    /** 内容容器额外类名。 */
    contentClassName?: string;
    /** 内容容器额外 style。 */
    contentStyle?: CSSProperties;
    children: ReactNode;
}

const STORAGE_PREFIX = "magic-md/resizable-viewport/";

function readStoredHeight(key: string | undefined): number | null {
    if (!key) return null;
    try {
        const raw = localStorage.getItem(STORAGE_PREFIX + key);
        if (!raw) return null;
        const n = Number(raw);
        return Number.isFinite(n) && n > 0 ? n : null;
    } catch {
        return null;
    }
}

function writeStoredHeight(key: string | undefined, height: number) {
    if (!key) return;
    try {
        localStorage.setItem(STORAGE_PREFIX + key, String(Math.round(height)));
    } catch {
        // ignore quota / privacy mode errors
    }
}

interface DragHandleProps {
    onMouseDown: (e: React.MouseEvent) => void;
    title: string;
    position: "top" | "bottom";
}

function DragHandle({ onMouseDown, title, position }: DragHandleProps) {
    const borderClass = position === "top" ? "border-b" : "border-t";
    return (
        <button
            type="button"
            onMouseDown={onMouseDown}
            className={`flex w-full items-center justify-center h-3 cursor-row-resize ${borderClass} border-border bg-muted/30 hover:bg-muted/60 transition-colors select-none group outline-none`}
            title={title}
        >
            <GripIcon className="text-muted-foreground/40 group-hover:text-muted-foreground/70" />
        </button>
    );
}

export function ResizableViewport({
    fillAvailable = false,
    defaultHeight = 400,
    min = 100,
    max,
    height,
    onHeightChange,
    onHeightCommit,
    storageKey,
    handlePosition = "bottom",
    blockPointerWhileDragging = true,
    title,
    className,
    contentClassName,
    contentStyle,
    children,
}: ResizableViewportProps) {
    const t = usePluginSdkT();
    const resolvedTitle = title ?? t("resizableViewport.title");
    const isControlled = height !== undefined;

    const [internalHeight, setInternalHeight] = useState<number>(() => {
        if (isControlled) return height as number;
        const stored = readStoredHeight(storageKey);
        return stored ?? defaultHeight;
    });
    const [isDragging, setIsDragging] = useState(false);
    const dragState = useRef<{
        startY: number;
        startH: number;
        direction: 1 | -1;
    } | null>(null);

    const currentHeight = isControlled ? (height as number) : internalHeight;

    const clamp = useCallback(
        (n: number) => {
            let v = Math.max(min, n);
            if (typeof max === "number") v = Math.min(max, v);
            return v;
        },
        [min, max],
    );

    const setHeight = useCallback(
        (next: number) => {
            const v = clamp(next);
            if (!isControlled) setInternalHeight(v);
            onHeightChange?.(v);
        },
        [clamp, isControlled, onHeightChange],
    );

    const handleDragStart = useCallback(
        (direction: 1 | -1) => (e: React.MouseEvent) => {
            e.preventDefault();
            dragState.current = {
                startY: e.clientY,
                startH: currentHeight,
                direction,
            };
            setIsDragging(true);
        },
        [currentHeight],
    );

    useEffect(() => {
        if (!isDragging) return;
        const onMove = (e: MouseEvent) => {
            const st = dragState.current;
            if (!st) return;
            const delta = (e.clientY - st.startY) * st.direction;
            setHeight(st.startH + delta);
        };
        const onUp = () => {
            const st = dragState.current;
            dragState.current = null;
            setIsDragging(false);
            if (st) {
                // Commit: persist to storage + fire callback
                const finalHeight = isControlled
                    ? (height as number)
                    : internalHeight;
                writeStoredHeight(storageKey, finalHeight);
                onHeightCommit?.(finalHeight);
            }
        };
        window.addEventListener("mousemove", onMove);
        window.addEventListener("mouseup", onUp);
        return () => {
            window.removeEventListener("mousemove", onMove);
            window.removeEventListener("mouseup", onUp);
        };
    }, [isDragging, setHeight, storageKey, onHeightCommit, isControlled, height, internalHeight]);

    const showTop = !fillAvailable && (handlePosition === "top" || handlePosition === "both");
    const showBottom = !fillAvailable && (handlePosition === "bottom" || handlePosition === "both");

    // Only apply default `overflow-hidden` when the consumer didn't specify
    // their own overflow- utility. Tailwind emits `overflow-hidden` after
    // `overflow-auto` in its stylesheet, so a class-based merge would always
    // lose the cascade race; we suppress the default instead.
    const consumerHasOverflow = /(^|\s)overflow(-[xy])?-/.test(
        contentClassName ?? "",
    );
    const overflowClass = consumerHasOverflow ? "" : "overflow-hidden";

    return (
        <div className={`${fillAvailable ? "h-full min-h-0 flex-1" : ""} ${className ?? ""}`.trim()}>
            {showTop && (
                <DragHandle
                    onMouseDown={handleDragStart(-1)}
                    title={resolvedTitle}
                    position="top"
                />
            )}
            <div
                className={`relative ${fillAvailable ? "h-full min-h-0" : ""} ${overflowClass} ${contentClassName ?? ""}`.trim()}
                style={fillAvailable
                    ? {
                        ...contentStyle,
                        height: "100%",
                        minHeight: 0,
                        transition: "none",
                    }
                    : {
                        height: currentHeight,
                        transition: isDragging ? "none" : "height 0.2s",
                        ...contentStyle,
                    }}
            >
                {blockPointerWhileDragging && isDragging && (
                    <div className="absolute inset-0 z-20" />
                )}
                {children}
            </div>
            {showBottom && (
                <DragHandle
                    onMouseDown={handleDragStart(1)}
                    title={resolvedTitle}
                    position="bottom"
                />
            )}
        </div>
    );
}
